import React, { useEffect, useState } from "react";
import {
  Accordions,
  Avatar,
  Box,
  Button,
  Dropdown,
  DropdownItem,
  getColor,
  Icons,
  InputField,
  Modal,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import { Member, Workgroup } from "../../types/workgroup";
import utilsCommon from "../../shared/utils/common";
import WorkgroupManagerIcon from "../WorkgroupManagerIcon";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import { nanoid } from "@reduxjs/toolkit";
import WorkgroupAddModal from "../WorkgroupAddModal";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import { t } from "i18next";

interface WorkgroupMemberTabProps {
  workgroupInfo?: Workgroup;
  isWorkgroupManager?: boolean;
}

const WorkgroupMemberTab: React.FC<WorkgroupMemberTabProps> = ({
  isWorkgroupManager,
  workgroupInfo,
}) => {
  const member_menu_types = [
    {
      resource: t("workgroup.action.transferMaster"),
      icon: <Icons variant="line" label="crown" />,
      key: "changeMaster",
      onClick: () => changeExtraModalMode("CHANGE_MASTER"),
    },
    {
      resource: t("workgroup.action.reinvite"),
      icon: <Icons variant="line" label="mail" />,
      key: "resentInvite",
      onClick: () => handleResendMail(),
    },
    {
      resource: t("workgroup.action.delete"),
      icon: <Icons variant="line" label="delete" />,
      key: "delete",
      onClick: () => changeExtraModalMode("REMOVE_MEMBER"),
    },
  ];
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const myId = loginDto?.userInfo?.userId ?? "";

  /** hook 정리 */
  const toasts = useToast();
  const { updateWorkgroupInfo, resendInviteMail } = useWorkgroupStore();

  /** State 정리 */
  const [targetUser, changeTargetUser] = useState<Member | undefined>(); // 워크그룹장 권한을 넘기고싶거나, 제거하고싶거나, 재초대 메일을 전송하고싶은 사용자
  const [extraModalMode, changeExtraModalMode] = useState("CLOSE"); // 추가 모달 리스트 열림 여부
  const [userList, changeUserList] = useState<{
    workgroupManager: Member[];
    internalUser: Member[];
    externalUser: Member[];
    unregisteredUser: Member[];
  }>({
    workgroupManager: [],
    internalUser: [],
    externalUser: [],
    unregisteredUser: [],
  }); // 워크그룹내 사용자 리스트
  const [keyword, changeKeyword] = useState(""); // 검색 키워드

  /** UseEffect 정리 */
  useEffect(() => {
    const manager = workgroupInfo?.members?.filter(
      (item) => item?.userId === workgroupInfo?.managerId
    );
    if (!manager) return;
    changeUserList({
      workgroupManager: manager ?? [],
      internalUser:
        workgroupInfo?.members?.filter(
          (item) =>
            item.userType === 1 &&
            item?.status === 1 &&
            manager[0]?.userId !== item?.userId
        ) ?? [],
      externalUser:
        workgroupInfo?.members?.filter(
          (item) => item.userType === 2 && item?.status === 1
        ) ?? [],
      unregisteredUser:
        workgroupInfo?.members?.filter(
          (item) => item?.status !== 1 || item.userType === 3
        ) ?? [],
    });
  }, [workgroupInfo]);

  /** 함수 정리 */
  /** 멤버 관련 메뉴 표시여부 확인 */
  const checkMenuValid = (key: string, member?: Member) => {
    /** 워크그룹장에겐 표시되지않음. */
    if (!member || member.userId === myId) return false;

    switch (key) {
      /** 권한 이관 */
      case "changeMaster":
        return member.userType === 1 && workgroupInfo?.workgroupType !== "HUB";
      /** 초대 재전송 */
      case "resentInvite":
        return (
          (member.userType === 2 && member.status === 2) ||
          member.userType === 3
        );
      /** 제거 */
      default:
        return true;
    }
  };
  /** 재초대 메일 전송 */
  const handleResendMail = async () => {
    if (!workgroupInfo || !targetUser) return;

    let resp: any = await resendInviteMail(
      workgroupInfo.workgroupId,
      targetUser.userId
    );
    if (resp.payload) {
      toasts.toastMsg(
        nanoid(),
        t("workgroup.alert.reinviteSuccess"),
        "success"
      );
    } else {
      toasts.toastMsg(
        nanoid(),
        resp.error?.message ?? t("common.alert.fail"),
        "error"
      );
    }
    changeTargetUser(undefined);
  };
  /** 권한 이관 */
  const handleChangeWorkgroupMaster = async () => {
    if (!workgroupInfo || !targetUser) return;

    let resp: any = await updateWorkgroupInfo({
      workgroupId: workgroupInfo.workgroupId,
      managerId: targetUser.userId,
    });
    if (resp.payload) {
      toasts.toastMsg(nanoid(), t("workgroup.alert.grantSuccess"), "success");
    } else {
      toasts.toastMsg(
        nanoid(),
        resp.error?.message ?? t("common.alert.fail"),
        "error"
      );
    }
    changeTargetUser(undefined);
    changeExtraModalMode("CLOSE");
  };
  /** 멤버 제거 */
  const handleRemoveUser = async () => {
    if (!workgroupInfo || !targetUser) return;
    let resp: any = await updateWorkgroupInfo({
      workgroupId: workgroupInfo?.workgroupId,
      workgroup: {
        ...workgroupInfo,
        members: [{ ...targetUser, changeCode: "DELETE" }],
      },
    });
    if (resp.payload) {
      toasts.toastMsg(nanoid(), t("workgroup.alert.removeSuccess"), "success");
    } else if (resp.error) {
      toasts.toastMsg(
        nanoid(),
        resp.error?.message ?? t("common.alert.fail"),
        "error"
      );
    }
    changeTargetUser(undefined);
    changeExtraModalMode("CLOSE");
  };
  /** UI: 사용자 리스트 표시 */
  const renderMemberInfo = (member: Member) => {
    const manager =
      workgroupInfo?.members?.filter(
        (item) => item?.userId === workgroupInfo?.managerId
      )[0]?.userId ?? "";
    return (
      <Box alignItems="center" height={50} classes="memberTag">
        <Avatar
          name={member?.userName ?? member?.userId}
          uniqueCode={utilsCommon.getUserColor(
            member?.userType,
            member?.status,
            member?.userName
          )}
          icon={member?.userName ? undefined : "user"}
          subLeftIcon={
            manager === member?.userId ? (
              <WorkgroupManagerIcon
                style={{
                  position: "absolute",
                  bottom: "-5px",
                  insetInlineEnd: "-12px",
                }}
              />
            ) : undefined
          }
          size={"32"}
        />
        <Box
          direction="column"
          classes="mg ml-8"
          width={isWorkgroupManager ? "calc(100% - 80px)" : "calc(100% - 60px)"}
        >
          <Tooltip
            message={utilsCommon.getUserNameIdTag(
              member?.userId,
              member?.userName,
              false,
              member.userType
            )}
          >
            <Typography type="body2_rg" ellipsis>
              {utilsCommon.getUserNameIdTag(
                member?.userId,
                member?.userName,
                false,
                member.userType
              )}
            </Typography>
          </Tooltip>
          <Tooltip
            message={utilsCommon.getUserPositionNDept({
              positionName: member?.positionName,
              deptName: member?.deptName,
              companyName: member?.companyName,
              userType: member?.userType,
            })}
          >
            <Typography
              type="caption1_rg"
              classes="mg mt-2"
              ellipsis
              exactColor={getColor("COLOR_TEXT_SECONDARY")}
            >
              {utilsCommon.getUserPositionNDept({
                positionName: member?.positionName,
                deptName: member?.deptName,
                companyName: member?.companyName,
                userType: member?.userType,
              })}
            </Typography>
          </Tooltip>
        </Box>
        {member?.userId !== myId && isWorkgroupManager && (
          <Dropdown
            btnIcon={<Icons variant="line" label="moreVertical" />}
            classes="mg ml-auto"
            btnSize="sm"
            positionStrategy="absolute"
            buttonWidth={190}
            align={"right"}
          >
            {member_menu_types
              .filter((item1) => checkMenuValid(item1.key, member))
              .map((item) => (
                <DropdownItem
                  frontIcon={item.icon}
                  label={item.resource}
                  onClick={() => {
                    changeTargetUser(member);
                    item.onClick();
                  }}
                  type={item.key === "delete" ? "warning" : "default"}
                />
              ))}
          </Dropdown>
        )}
      </Box>
    );
  };
  /** 모달: 권한 이관 확인 모달 */
  const renderChangeMasterModal = () => {
    return (
      <Modal
        open={true}
        onClose={() => changeExtraModalMode("CLOSE")}
        title={t("workgroup.grantAuthTitle")}
        width={540}
        bodyMsg={t("workgroup.grantAuthBody")}
        footer={
          <Box>
            <Button
              label={t("common.cancel")}
              btnStyle="fill"
              type="secondary"
              onClick={() => changeExtraModalMode("CLOSE")}
            />
            <Button
              label={t("workgroup.action.grant")}
              btnStyle="fill"
              onClick={handleChangeWorkgroupMaster}
            />
          </Box>
        }
      />
    );
  };
  /** 모달: 멤버 제거 확인 모달 */
  const renderRemoveMemberModal = () => {
    return (
      <Modal
        open={true}
        onClose={() => changeExtraModalMode("CLOSE")}
        title={t("workgroup.removeUserTitle")}
        width={540}
        bodyMsg={t("workgroup.removeUserBody")}
        footer={
          <Box>
            <Button
              label={t("common.cancel")}
              btnStyle="fill"
              type="secondary"
              onClick={() => changeExtraModalMode("CLOSE")}
            />
            <Button
              label={t("workgroup.action.delete")}
              btnStyle="fill"
              type="error"
              state="hover"
              onClick={handleRemoveUser}
            />
          </Box>
        }
      />
    );
  };
  return (
    <Box direction="column" classes="workroupMemberTab">
      {extraModalMode === "CHANGE_MASTER" && renderChangeMasterModal()}
      {extraModalMode === "REMOVE_MEMBER" && renderRemoveMemberModal()}
      {extraModalMode === "ADD_MEMBER" && (
        <WorkgroupAddModal
          createType={"ADD_NEW_MEMBER"}
          disableValues={workgroupInfo?.members.map((item) => item.userId)}
          open={extraModalMode === "ADD_MEMBER"}
          workgroupInfo={workgroupInfo}
          onClose={() => changeExtraModalMode("CLOSE")}
        />
      )}
      <Box alignItems="center">
        <Typography type="subtitle1">{t("common.member")}</Typography>
        <Typography
          type="subtitle1"
          classes="mg ml-6"
          exactColor={getColor("COLOR_TEXT_INFO")}
        >
          {
            workgroupInfo?.members.filter(
              (item1) =>
                item1.userId?.includes(keyword) ||
                item1.userName?.includes(keyword) ||
                item1.email?.includes(keyword)
            ).length
          }
        </Typography>
        {utilsCommon.checkMenuAvailble("inviteUser", workgroupInfo) && (
          <Button
            label={t("workgroup.addMember")}
            btnStyle="link"
            classes="mg ml-auto"
            onClick={() => changeExtraModalMode("ADD_MEMBER")}
            leadingIcon={<Icons variant="line" label="addPerson" />}
          />
        )}
      </Box>
      <InputField
        placeholder={t("common.userInputPlaceholder")}
        leadingIcon={<Icons variant="line" label="search" />}
        classes="mg mt-8 mb-8"
        size="md"
        value={keyword}
        onChange={(e) => changeKeyword(e.target.value)}
      />
      <Box
        direction="column"
        classes="overflow-scroll scroll-important"
        height={488}
        width={"calc(100% + 20px)"}
        style={{ paddingRight: "20px", boxSizing: "border-box" }}
      >
        <Accordions
          hasBorder={false}
          label={t("workgroup.manager")}
          initialOpenStatus
        >
          {userList.workgroupManager.filter(
            (item1) =>
              item1.userId?.includes(keyword) ||
              item1.userName?.includes(keyword) ||
              item1.email?.includes(keyword)
          )[0] &&
            renderMemberInfo(
              userList.workgroupManager.filter(
                (item1) =>
                  item1.userId?.includes(keyword) ||
                  item1.userName?.includes(keyword) ||
                  item1.email?.includes(keyword)
              )[0]
            )}
        </Accordions>
        <Accordions
          hasBorder={false}
          label={t("common.internalUser")}
          initialOpenStatus
          labelLeftComponent={
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_INFO")}
            >
              {
                userList.internalUser.filter(
                  (item1) =>
                    item1.userId?.includes(keyword) ||
                    item1.userName?.includes(keyword) ||
                    item1.email?.includes(keyword)
                )?.length
              }
            </Typography>
          }
        >
          {userList.internalUser
            ?.filter(
              (item1) =>
                item1.userId?.includes(keyword) ||
                item1.userName?.includes(keyword) ||
                item1.email?.includes(keyword)
            )
            ?.map((item) => renderMemberInfo(item))}
        </Accordions>
        <Accordions
          hasBorder={false}
          initialOpenStatus
          label={t("common.externalUser")}
          labelLeftComponent={
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_INFO")}
            >
              {
                userList.externalUser.filter(
                  (item1) =>
                    item1.userId?.includes(keyword) ||
                    item1.userName?.includes(keyword) ||
                    item1.email?.includes(keyword)
                ).length
              }
            </Typography>
          }
        >
          {userList.externalUser
            ?.filter(
              (item1) =>
                item1.userId?.includes(keyword) ||
                item1.userName?.includes(keyword) ||
                item1.email?.includes(keyword)
            )
            ?.map((item) => renderMemberInfo(item))}
        </Accordions>
        <Accordions
          initialOpenStatus
          hasBorder={false}
          label={t("common.noJoinUser")}
          labelLeftComponent={
            <Typography
              type="subtitle2"
              exactColor={getColor("COLOR_TEXT_INFO")}
            >
              {
                userList.unregisteredUser?.filter(
                  (item1) =>
                    item1.userId?.includes(keyword) ||
                    item1.userName?.includes(keyword) ||
                    item1.email?.includes(keyword)
                )?.length
              }
            </Typography>
          }
        >
          {userList.unregisteredUser
            ?.filter(
              (item1) =>
                item1.userId?.includes(keyword) ||
                item1.userName?.includes(keyword) ||
                item1.email?.includes(keyword)
            )
            ?.map((item) => renderMemberInfo(item))}
        </Accordions>
      </Box>
    </Box>
  );
};
export default WorkgroupMemberTab;
